import styled, { css } from 'styled-components';
import { Typography } from '~/ui/components';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  .ant-tabs {
    padding: 0;
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
`;

export const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  @media (max-width: 485px) {
    flex-direction: column;
  }
`;

type ContentProps = {
  end?: boolean;
  clickable?: boolean;
};

export const Content = styled.div<ContentProps>`
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

  svg {
    color: ${({ theme }) => theme.colors.brand.primary};
  }

  ${({ end }) =>
    end &&
    css`
      justify-content: end;
    `};

  @media (max-width: 485px) {
    justify-content: space-between;
  }
`;

export const OpenTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px solid ${({ theme }) => theme.colors.brand.primary};
  color: ${({ theme }) => theme.colors.brand.primary};
  border-radius: 10px;
  padding-block: 2px;
  width: fit-content;
  padding-inline: 12px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;

  /* The drawer header forces every button to 25x25; restore the resolve button to its real size. */
  .header-resolve-btn {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
    height: 34px;
    max-height: 34px;
  }
`;

export const TagsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ChannelTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px solid ${({ theme }) => theme.colors.alert.primary};
  color: ${({ theme }) => theme.colors.alert.primary};
  background: ${({ theme }) => theme.colors.alert.secondary};
  border-radius: 10px;
  padding-block: 2px;
  width: fit-content;
  padding-inline: 12px;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  button {
    min-width: fit-content;
  }

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

export const GriContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
  flex: 1;
  width: 100%;

  .info-grid {
    gap: 24px 12px;
    span {
      font-size: 16px;
      font-weight: 600;
    }
  }
`;

export const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.brand.primary};
  }
`;

export const PersonLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  margin-left: 20px;
  padding-left: 20px;
  border-left: 1.5px solid ${({ theme }) => theme.colors.element.disabled};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.element.secondary};
`;

export const PersonLinkId = styled.a`
  color: ${({ theme }) => theme.colors.brand.primary};
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.brand.primary};
  }
`;

export const TopTabsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.element.disabled};
  margin-bottom: 16px;
  padding-right: 0;
`;

export const TopTabItem = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.brand.primary : 'transparent')};
  cursor: pointer;
  padding: 30px 18px;
  margin-bottom: -1px;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? '600' : 'normal')};
  color: ${({ $active, theme }) => ($active ? theme.colors.brand.primary : theme.colors.element.secondary)};
  white-space: nowrap;

  &:focus {
    outline: none;
  }
`;

export const SlaInfo = styled(Typography).attrs({
  type: 'bodyXSmall',
  weight: '400',
  element: 'div',
  color: 'element.secondary',
})`
  display: flex;
  align-items: center;
  gap: 8px;

  b {
    color: ${({ theme }) => theme.colors.element.primary};
    font-size: ${({ theme }) => theme.typography.types.bodySmall.size};
    font-weight: 600;
  }
`;

// badge de origem do ticket ao lado do título — ícone da origem ou "t.";
// tooltip explica a origem
export const OriginBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border: 1px solid ${({ theme }) => theme.colors.brand.primary};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.brand.primary};
  font-size: 12px;
  font-weight: 700;
  cursor: default;

  svg {
    width: 14px;
    height: 14px;
    color: ${({ theme }) => theme.colors.brand.primary};
  }
`;

// bloco da demanda (modo embutido): título do motivo + descrição + último atendimento
export const DemandBlock = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DemandTitle = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.element.primary};
`;

export const DemandText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.element.secondary};
`;

// label das seções da demanda — mesmo ritmo visual de "CONTRATOS VINCULADOS"
export const DemandLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.element.secondary};
  margin-bottom: 4px;

  span {
    font-weight: 400;
    letter-spacing: normal;
    text-transform: none;
  }
`;

// painel destacado do último atendimento (fundo suave, sem borda)
export const LastServicePanel = styled.div`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 8px;
  padding: 10px 12px;
`;

// seção "Contrato vinculado" (modo embutido) — lista os contratos do ticket
export const ContractSection = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ContractSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.element.secondary};
`;

// item de contrato (acordeão): fundo um pouco mais escuro, sem bordas
export const ContractItem = styled.div`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 8px;
  overflow: hidden;
`;

export const ContractItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
`;

// contrato e produto na mesma linha
export const ContractItemInfo = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 20px;
`;

export const ContractInline = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.element.primary};

  em {
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.element.secondary};
  }
`;

export const ContractToggle = styled.button<{ $open?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.brand.primary};
  font-size: 13px;
  font-weight: 600;

  svg {
    transition: transform 0.15s;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  }

  &:focus {
    outline: none;
  }
`;

export const ContractDetail = styled.div`
  padding: 0 12px 12px;
`;
